import type { ReactNode } from 'react'
import { TrustGauge } from '../trust-gauge/TrustGauge.tsx'
import type { IndexCompetition, IndexItem } from './types'
import { KIND_LABELS, competitionStatus, followLink, itemSubtitle, splitMatch, tileTextColor, tileName } from './view'
import styles from './IndexSearch.module.css'

/** A logo, or a lettered tile in the stored brand colour when there isn't one. */
export function IndexTile({
  name,
  logoUrl,
  color,
  round = false,
  size = 'md',
}: {
  name: string
  logoUrl?: string | null
  color?: string
  round?: boolean
  size?: 'sm' | 'md' | 'lg'
}) {
  const shape = `${styles.tile} ${styles[`tile_${size}`]} ${round ? styles.tileRound : ''}`
  if (logoUrl) {
    return <img className={shape} src={logoUrl} alt="" />
  }
  return (
    <span
      className={`${shape} ${tileTextColor(color) === 'dark' ? styles.tileDarkText : ''}`}
      style={color ? { background: color } : undefined}
      aria-hidden="true"
    >
      {name.charAt(0).toUpperCase()}
    </span>
  )
}

export function StatusBadge({ competition }: { competition: IndexCompetition }) {
  const status = competitionStatus(competition)
  return (
    <span className={`${styles.status} ${styles[`status_${status.tone}`]}`}>
      {status.tone === 'live' && <span className={styles.liveDot} aria-hidden="true" />}
      {status.label}
    </span>
  )
}

/** Status as coloured text with the game, for rows too narrow for a badge. */
function CompactStatus({ competition }: { competition: IndexCompetition }) {
  const status = competitionStatus(competition)
  return (
    <span className={styles.rowSub}>
      <span className={styles[`statusText_${status.tone}`]}>
        {status.tone === 'live' && <span className={styles.liveDot} aria-hidden="true" />}
        {status.label}
      </span>
      {' · '}
      {competition.gameName}
    </span>
  )
}

export function Highlight({ name, query }: { name: string; query: string }) {
  const { before, match, after } = splitMatch(name, query)
  if (!match) return <>{name}</>
  return (
    <>
      {before}
      <mark className={styles.match}>{match}</mark>
      {after}
    </>
  )
}

function RowMeta({ item, compact }: { item: IndexItem; compact: boolean }): ReactNode {
  // A shelf row is narrow: its status rides in the subtitle instead.
  if (item.kind === 'competition') return compact ? null : <StatusBadge competition={item} />
  if (item.kind === 'player') {
    if (item.isGuest) return <span className={styles.kindLabel}>Guest</span>
    if (item.trustScore != null) return <TrustGauge score={item.trustScore} size={32} />
  }
  return null
}

export interface IndexRowProps {
  item: IndexItem
  query: string
  /** DOM id, so the search box can point at it with aria-activedescendant. */
  id: string
  active: boolean
  /** Label the kind on the right — for lists that mix kinds. */
  showKind?: boolean
  compact?: boolean
  onNavigate: (href: string) => void
  onHover: () => void
}

export function IndexRow({ item, query, id, active, showKind = false, compact = false, onNavigate, onHover }: IndexRowProps) {
  return (
    <a
      id={id}
      href={item.href}
      className={`${styles.row} ${compact ? styles.rowCompact : ''}`}
      data-active={active || undefined}
      aria-current={active || undefined}
      onClick={(e) => followLink(e, item.href, onNavigate)}
      onMouseEnter={onHover}
    >
      <IndexTile
        name={tileName(item)}
        logoUrl={item.logoUrl}
        color={item.color}
        round={item.kind === 'player'}
        size={compact ? 'sm' : 'md'}
      />
      <span className={styles.rowText}>
        <span className={styles.rowName}>
          <Highlight name={item.name} query={query} />
        </span>
        {compact && item.kind === 'competition' ? (
          <CompactStatus competition={item} />
        ) : (
          <span className={styles.rowSub}>{itemSubtitle(item)}</span>
        )}
      </span>
      <RowMeta item={item} compact={compact} />
      {showKind && <span className={styles.kindLabel}>{KIND_LABELS[item.kind]}</span>}
    </a>
  )
}
